import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Paper
} from '@mui/material';
import apiService from '../../services/apiService';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

/**
 * Componente para diagnosticar y depurar problemas relacionados con la API financiera
 * Principalmente útil para resolver problemas con los endpoints de sندات القبض (recibos)
 */
const FinancialDebugger: React.FC = () => {
  const [apiStatus, setApiStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [apiMessage, setApiMessage] = useState<string>('');
  const [debugData, setDebugData] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkApiStatus();
  }, []);

  const checkApiStatus = async () => {
    setApiStatus('loading');
    setApiMessage('جاري التحقق من حالة واجهة برمجة التطبيقات...');
    
    try {
      // Send a simple request to check if the API is responding
      const response = await axios.get(`${API_BASE_URL}/auth/check-status`, {
        timeout: 5000
      }).catch(() => null);
      
      if (response && response.status === 200) {
        setApiStatus('success');
        setApiMessage('واجهة برمجة التطبيقات تعمل بشكل صحيح');
      } else {
        setApiStatus('error');
        setApiMessage('فشل الاتصال بواجهة برمجة التطبيقات. تأكد من تشغيل الخادم.');
      }
    } catch (error) {
      console.error('API check error:', error);
      setApiStatus('error');
      setApiMessage(`فشل الاتصال بواجهة برمجة التطبيقات: ${error}`);
    }
  };

  const testFetchReceipts = async (endpoint: string) => {
    setLoading(true);
    try {
      // Try a direct axios call instead of using the apiService
      // to bypass any potential issues with the service
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, { 
        headers,
        timeout: 10000
      });
      
      setDebugData({
        ...debugData,
        [endpoint]: {
          status: response.status,
          statusText: response.statusText,
          data: response.data,
          headers: response.headers,
          timestamp: new Date().toISOString()
        }
      });
      
      return response;
    } catch (error: any) {
      console.error(`Error testing ${endpoint}:`, error);
      setDebugData({
        ...debugData,
        [endpoint]: {
          error: true,
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          timestamp: new Date().toISOString()
        }
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createTestReceipt = async () => {
    setLoading(true);
    try {
      // Get accounts first to use real IDs
      const accountsResponse = await axios.get(`${API_BASE_URL}/finance/accounts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      let accounts = accountsResponse.data;
      if (Array.isArray(accountsResponse.data.accounts)) {
        accounts = accountsResponse.data.accounts;
      }
      
      // Use the first two accounts if available
      const debitAccountId = accounts.length > 0 ? accounts[0].id : '123456789012345678901234';
      const creditAccountId = accounts.length > 1 ? accounts[1].id : '123456789012345678901235';
      
      const testReceipt = {
        debitAccount: debitAccountId,
        creditAccount: creditAccountId,
        amount: 1000,
        description: "سند اختبار للتشخيص",
        date: new Date().toISOString().split('T')[0]
      };
      
      const response = await apiService.post('/api/receipts', testReceipt);
      
      setDebugData({
        ...debugData,
        createTestReceipt: {
          success: true,
          data: response,
          timestamp: new Date().toISOString()
        }
      });
      
      // After creating, fetch the list again
      await testFetchReceipts('/api/receipts');
      
    } catch (error: any) {
      console.error('Error creating test receipt:', error);
      setDebugData({
        ...debugData,
        createTestReceipt: {
          error: true,
          message: error.message,
          response: error.response?.data,
          timestamp: new Date().toISOString()
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const runAllTests = async () => {
    setLoading(true);
    await checkApiStatus();
    await testFetchReceipts('/api/receipts');
    await testFetchReceipts('/api/finance/receipts');
    setLoading(false);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        أداة تشخيص المشاكل المالية
      </Typography>
      
      <Alert severity={
        apiStatus === 'loading' ? 'info' :
        apiStatus === 'success' ? 'success' : 'error'
      } sx={{ mb: 2 }}>
        {apiMessage}
      </Alert>
      
      <Box sx={{ mb: 2 }}>
        <Button 
          variant="outlined"
          color="primary"
          onClick={runAllTests}
          disabled={loading}
          sx={{ mr: 1 }}
        >
          فحص شامل
        </Button>
        
        <Button 
          variant="outlined"
          color="info"
          onClick={() => testFetchReceipts('/api/receipts')}
          disabled={loading}
          sx={{ mr: 1 }}
        >
          اختبار /api/receipts
        </Button>
        
        <Button 
          variant="outlined"
          color="info"
          onClick={() => testFetchReceipts('/api/finance/receipts')}
          disabled={loading}
          sx={{ mr: 1 }}
        >
          اختبار /api/finance/receipts
        </Button>
        
        <Button 
          variant="contained"
          color="secondary"
          onClick={createTestReceipt}
          disabled={loading}
        >
          إنشاء سند اختبار
        </Button>
      </Box>
      
      {loading && (
        <Box display="flex" justifyContent="center" sx={{ my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle1" gutterBottom>
        نتائج التشخيص:
      </Typography>
      
      <Box sx={{ 
        backgroundColor: '#f5f5f5', 
        p: 2, 
        borderRadius: 1,
        maxHeight: '300px',
        overflowY: 'auto',
        fontFamily: 'monospace',
        fontSize: '0.875rem'
      }}>
        <pre>{JSON.stringify(debugData, null, 2)}</pre>
      </Box>
    </Paper>
  );
};

export default FinancialDebugger;